import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Pencil,
  Save,
  X,
  RefreshCw,
  ChevronLeft,
  Calendar,
  Users,
  ShieldCheck,
  Trophy,
  Trash2,
  CheckCircle2,
  Lock,
  History,
  Settings,
  ArrowRight,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";

import {
  useAssignLeadershipMutation,
  useCloseChapterDietMutation,
  useGetChapterDietDetailsQuery,
  useGetChaptersQuery,
  useRemoveLeadershipMutation,
  useUpdateChapterDietMutation,
} from "../api/chapterApi";
import MemberPicker from "../components/MemberPicker";

// --- Reusable Section Component ---
function Section({
  title,
  icon: Icon,
  children,
  badge,
}: {
  title: string;
  icon: any;
  children: any;
  badge?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all h-full">
      <div className="flex items-center justify-between mb-6 border-b border-gray-50 pb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-indigo-500" />
          <div className="font-black text-gray-900 text-[10px] uppercase tracking-[0.2em]">
            {title}
          </div>
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function isActive(d: any) {
  return d?.is_active === true || d?.is_active === 1;
}

// A rejected assignment can still reach us on a 2xx status — the host rewrites
// the code the API set (422 → 202) but the body keeps {status: "error"}. Read
// the body, otherwise a refused chairman reads as "assigned successfully".
function apiErrorMessage(payload: any): string | null {
  if (!payload || typeof payload !== "object") return null;
  if (payload.status !== "error" && !payload.error) return null;
  return payload.message || payload.error || "Request failed";
}

// The other half of the same problem: a real HTTP error carries the message in
// err.data, a mangled one in the resolved payload.
function errorText(err: any, fallback: string): string {
  return (
    apiErrorMessage(err?.data) ||
    err?.data?.message ||
    apiErrorMessage(err) ||
    err?.message ||
    fallback
  );
}

export default function ChapterDietDetailsPage() {
  const { id } = useParams();
  const dietId = String(id || "");
  const { can } = useAccess();

  // Queries
  const {
    data: diet,
    isLoading,
    error,
    refetch,
  } = useGetChapterDietDetailsQuery(dietId, { skip: !dietId });
  const { data: chapters = [] } = useGetChaptersQuery();

  // Mutations
  const [updateDiet, { isLoading: savingDiet }] =
    useUpdateChapterDietMutation();
  const [closeDiet, { isLoading: closing }] = useCloseChapterDietMutation();
  const [assign, { isLoading: assigning }] = useAssignLeadershipMutation();
  const [removeLeadership, { isLoading: removing }] =
    useRemoveLeadershipMutation();

  // Local States
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [edit, setEdit] = useState({ name: "", start_date: "", end_date: "" });
  const [assignForm, setAssignForm] = useState({
    chapter_id: "",
    chairman_id: null as number | null,
    secretary_id: null as number | null,
  });

  // The banner lives above the assignment form — a refusal triggered from the
  // bottom of that form would otherwise scroll off-screen unnoticed.
  const msgRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (msg?.type === "error") {
      msgRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [msg]);

  useEffect(() => {
    if (!diet) return;
    setEdit({
      name: diet.name || "",
      start_date: (diet.start_date || "").slice(0, 10),
      end_date: (diet.end_date || "").slice(0, 10),
    });
  }, [diet]);

  const leaderships = useMemo(() => diet?.leaderships || [], [diet]);
  const busy = savingDiet || closing || assigning || removing;
  const active = isActive(diet);

  // Handlers
  const onSaveDiet = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      const res = await updateDiet({
        id: dietId,
        name: edit.name,
        start_date: edit.start_date || null,
        end_date: edit.end_date || null,
      }).unwrap();
      const rejected = apiErrorMessage(res);
      if (rejected) {
        return setMsg({ type: "error", text: rejected });
      }
      setMsg({ type: "success", text: "Diet schedule updated successfully" });
      refetch();
    } catch (err: any) {
      setMsg({ type: "error", text: errorText(err, "Update failed") });
    }
  };

  const onClose = async () => {
    if (
      !window.confirm(
        "Close this diet? This will lock all leadership assignments."
      )
    )
      return;
    setMsg(null);
    try {
      const res = await closeDiet(dietId).unwrap();
      const rejected = apiErrorMessage(res);
      if (rejected) {
        return setMsg({ type: "error", text: rejected });
      }
      setMsg({ type: "success", text: "Diet has been closed" });
      refetch();
    } catch (err: any) {
      setMsg({ type: "error", text: errorText(err, "Close failed") });
    }
  };

  const onAssign = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!assignForm.chapter_id) {
      return setMsg({ type: "error", text: "Please select a chapter" });
    }

    if (!assignForm.chairman_id && !assignForm.secretary_id) {
      return setMsg({
        type: "error",
        text: "Pick at least a chairman or secretary",
      });
    }

    if (assignForm.chairman_id === assignForm.secretary_id) {
      return setMsg({
        type: "error",
        text: "Chairman and Secretary cannot be the same person.",
      });
    }

    try {
      const res = await assign({
        chapter_id: assignForm.chapter_id,
        chapter_diet_id: dietId,
        chairman_id: assignForm.chairman_id,
        secretary_id: assignForm.secretary_id,
      }).unwrap();

      // Nothing was assigned — keep the form as the user left it so the
      // rejected pick can be corrected instead of re-entered.
      const rejected = apiErrorMessage(res);
      if (rejected) {
        return setMsg({ type: "error", text: rejected });
      }

      setMsg({
        type: "success",
        text: res?.message || "Leadership assigned successfully",
      });
      setAssignForm({ chapter_id: "", chairman_id: null, secretary_id: null });
      refetch();
    } catch (err: any) {
      setMsg({ type: "error", text: errorText(err, "Assignment failed") });
    }
  };

  const onRemove = async (leadershipId: string) => {
    if (!window.confirm("Remove this leadership record?")) return;
    try {
      const res = await removeLeadership(leadershipId).unwrap();
      const rejected = apiErrorMessage(res);
      if (rejected) {
        return setMsg({ type: "error", text: rejected });
      }
      setMsg({ type: "success", text: "Record removed" });
      refetch();
    } catch (err: any) {
      setMsg({ type: "error", text: errorText(err, "Failed to remove") });
    }
  };

  return (
    <PageGate perm={PERMS.chapter.detailsAccess}>
      <div className="space-y-6 pb-20">
        {isLoading ? (
          <div className="py-20 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-200" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-6 rounded-3xl border border-red-100 text-center font-bold">
            Failed to load diet details.
          </div>
        ) : (
          <>
            {/* 1. TOP QUICK STATS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    active
                      ? "bg-green-50 text-green-600"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                    Status
                  </div>
                  <div
                    className={`text-xs font-black uppercase tracking-tighter ${
                      active ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {active ? "Live Session" : "Archived"}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                    Chapters
                  </div>
                  <div className="text-lg font-black text-gray-900 tracking-tighter">
                    {leaderships.length} Active
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                    Timeline
                  </div>
                  <div className="text-xs font-black text-gray-900 tracking-tighter uppercase">
                    {diet?.start_date
                      ? new Date(diet.start_date).getFullYear()
                      : "—"}
                    <ArrowRight className="inline w-3 h-3 mx-1 text-gray-300" />
                    {diet?.end_date
                      ? new Date(diet.end_date).getFullYear()
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                    Governance
                  </div>
                  <div className="text-xs font-black text-gray-900 uppercase tracking-tighter">
                    Verified Term
                  </div>
                </div>
              </div>
            </div>

            {/* 2. HEADER CARD */}
            <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
                    <History className="w-8 h-8" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">
                      {diet?.name}
                    </h1>
                    <p className="text-gray-400 text-sm mt-2 font-medium">
                      Operational Diet Period • Ref:{" "}
                      <span className="font-bold text-indigo-500">
                        {dietId}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Link
                    to="/chapter/diets"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all"
                  >
                    <ChevronLeft className="w-4 h-4 text-indigo-500" /> Back
                  </Link>
                  <button
                    onClick={() => refetch()}
                    className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                  >
                    <RefreshCw
                      className={`w-4 h-4 text-indigo-500 ${
                        isLoading && "animate-spin"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {msg && (
                <div
                  ref={msgRef}
                  className={`mt-6 p-4 rounded-2xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
                    msg.type === "success"
                      ? "bg-green-50 border-green-100 text-green-800"
                      : "bg-red-50 border-red-100 text-red-800"
                  }`}
                >
                  <span className="text-xs font-black uppercase tracking-widest">
                    {msg.text}
                  </span>
                  <button
                    onClick={() => setMsg(null)}
                    className="opacity-50 hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* 3. MAIN CONTENT GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Diet Configuration Section */}
              <div className="lg:col-span-1">
                <Section title="Diet Configuration" icon={Settings}>
                  <form onSubmit={onSaveDiet} className="space-y-6">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Term Identity
                      </label>
                      <input
                        value={edit.name}
                        onChange={(e) =>
                          setEdit((s) => ({ ...s, name: e.target.value }))
                        }
                        className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 transition-colors disabled:opacity-40 bg-transparent"
                        disabled={!active || busy}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          Commencement
                        </label>
                        <input
                          type="date"
                          value={edit.start_date}
                          onChange={(e) =>
                            setEdit((s) => ({
                              ...s,
                              start_date: e.target.value,
                            }))
                          }
                          className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 transition-colors disabled:opacity-40 bg-transparent"
                          disabled={!active || busy}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          Conclusion
                        </label>
                        <input
                          type="date"
                          value={edit.end_date}
                          onChange={(e) =>
                            setEdit((s) => ({ ...s, end_date: e.target.value }))
                          }
                          className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 transition-colors disabled:opacity-40 bg-transparent"
                          disabled={!active || busy}
                        />
                      </div>
                    </div>

                    <AccessGate perm={PERMS.chapter.manageDiet}>
                      <div className="pt-6 space-y-3">
                        <button
                          type="submit"
                          disabled={!active || busy}
                          className="w-full bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-gray-800 transition-all shadow-lg shadow-gray-100 disabled:opacity-30 active:scale-95"
                        >
                          {savingDiet ? "Updating..." : "Synchronize Schedule"}
                        </button>
                        {active && (
                          <button
                            type="button"
                            onClick={onClose}
                            className="w-full border-2 border-red-50 text-red-600 rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-red-50 transition-all flex items-center justify-center gap-2"
                          >
                            <Lock className="w-3 h-3" /> Finalize & Close
                            Session
                          </button>
                        )}
                      </div>
                    </AccessGate>
                  </form>
                </Section>
              </div>

              {/* Assign Leadership Section */}
              <div className="lg:col-span-2">
                <Section
                  title="Executive Assignment"
                  icon={Users}
                  badge={
                    !active ? (
                      <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border border-red-100">
                        Locked
                      </span>
                    ) : (
                      <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border border-green-100">
                        Live
                      </span>
                    )
                  }
                >
                  <form
                    onSubmit={onAssign}
                    className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                  >
                    <div className="md:col-span-2 space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Target Chapter
                      </label>
                      <select
                        value={assignForm.chapter_id}
                        onChange={(e) =>
                          setAssignForm((s) => ({
                            ...s,
                            chapter_id: e.target.value,
                          }))
                        }
                        disabled={!active || busy}
                        className="w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 transition-colors disabled:opacity-30 bg-transparent"
                      >
                        <option value="">Select organizational unit...</option>
                        {chapters.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name} — {c?.state_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <MemberPicker
                      label="Chairman Designation"
                      valueUserId={assignForm.chairman_id}
                      onPickUserId={(id) =>
                        setAssignForm((s) => ({ ...s, chairman_id: id }))
                      }
                      disabled={!active || busy}
                    />

                    <MemberPicker
                      label="Secretariat Designation"
                      valueUserId={assignForm.secretary_id}
                      onPickUserId={(id) =>
                        setAssignForm((s) => ({ ...s, secretary_id: id }))
                      }
                      disabled={!active || busy}
                    />

                    <div className="md:col-span-2 flex justify-end pt-6 border-t border-gray-50 mt-4">
                      <AccessGate perm={PERMS.chapter.assignLeadership}>
                        <button
                          disabled={!active || busy}
                          className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all disabled:opacity-50 active:scale-95"
                        >
                          <CheckCircle2 className="w-4 h-4" /> Deploy Leadership
                        </button>
                      </AccessGate>
                    </div>
                  </form>
                </Section>
              </div>

              {/* Leadership List - Full Width Table */}
              <div className="lg:col-span-3">
                <div className="bg-white border border-gray-100 rounded-[2.5rem] overflow-hidden shadow-sm">
                  <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-600 rounded-lg text-white">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-black text-gray-900 text-sm uppercase tracking-tighter">
                          Current Leadership Roster
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                          Confirmed Chapter Executives
                        </p>
                      </div>
                    </div>
                    <div className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-4 py-2 rounded-full uppercase tracking-widest">
                      {leaderships.length} Assignments
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left bg-white border-b border-gray-50">
                          <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Chapter Unit
                          </th>
                          <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Chairman
                          </th>
                          <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Secretary
                          </th>
                          <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {leaderships.map((l: any) => (
                          <tr
                            key={l.id}
                            className="group hover:bg-gray-50/50 transition-all"
                          >
                            <td className="px-8 py-5">
                              <div className="font-black text-gray-900 group-hover:text-indigo-600 transition-colors">
                                {l.chapter?.name}
                              </div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                {l.chapter?.state}
                              </div>
                            </td>
                            <td className="px-8 py-5">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-black border border-blue-100">
                                  {l.chairman?.name?.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-black text-gray-800">
                                    {l.chairman?.name || "—"}
                                  </div>
                                  <div className="text-[10px] font-bold text-gray-400">
                                    {l.chairman?.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-5">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xs font-black border border-purple-100">
                                  {l.secretary?.name?.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-black text-gray-800">
                                    {l.secretary?.name || "—"}
                                  </div>
                                  <div className="text-[10px] font-bold text-gray-400">
                                    {l.secretary?.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-5 text-right">
                              <AccessGate perm={PERMS.chapter.removeLeadership}>
                                <button
                                  onClick={() => onRemove(String(l.id))}
                                  disabled={busy}
                                  className="p-3 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-30 active:scale-95"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </AccessGate>
                            </td>
                          </tr>
                        ))}
                        {!leaderships.length && (
                          <tr>
                            <td colSpan={4} className="px-8 py-24 text-center">
                              <div className="flex flex-col items-center justify-center space-y-3 opacity-20">
                                <ShieldCheck className="w-12 h-12" />
                                <p className="text-[10px] font-black uppercase tracking-[0.3em]">
                                  No Leadership Records Found
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Administrative Audit Footer */}
              <div className="lg:col-span-3 bg-gray-900 text-white rounded-[2rem] p-10 flex flex-col md:flex-row items-center gap-8 shadow-2xl shadow-gray-200">
                <div className="w-20 h-20 bg-indigo-600/20 rounded-3xl flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-10 h-10 text-indigo-400" />
                </div>
                <div>
                  <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
                    Institutional Governance Protocol
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed max-w-4xl font-medium">
                    Closing a diet session is an{" "}
                    <span className="text-white underline decoration-indigo-500 underline-offset-4 font-bold">
                      irreversible institutional action
                    </span>
                    . Once a session is finalized, the system locks all
                    executive assignments for that term. This ensures the
                    integrity of organizational history and prevents
                    retrospective manipulation of leadership records.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </PageGate>
  );
}
